<template>
    <CompassDialog
        :visible.sync="showModal"
        size="small"
        :title="$t.__('bankreconciliation.TransferTransactionForm.transfer_transactions')"
        class="transfer-transaction-form"
        @closed="close"
    >
        <template slot="default">
            <div class="dialog__default">
                <p class="grey mb-3">
                    {{
                        $l10n.__(
                            "Vous pouvez transférer des transactions d'un compte vers un autre.",
                        )
                    }}
                    <br />
                    {{
                        $l10n.__(
                            'Permet de répondre à des problématiques de doublon de compte via les problèmes de synchronisation de Budget Insight.',
                        )
                    }}
                </p>
                <el-form
                    ref="form"
                    v-loading="loading"
                    :model="form"
                    :rules="rules"
                    labelPosition="top"
                >
                    <el-form-item prop="from_bank_account_id">
                        <template slot="label">
                            <span class="bold">
                                {{ $l10n.__('Je souhaite transférer les éléments du compte') }}
                            </span>
                        </template>
                        <el-select
                            v-model="form.from_bank_account_id"
                            class="wp-10"
                            :placeholder="
                                $t.__('bankreconciliation.TransferTransactionForm.select_account')
                            "
                        >
                            <el-option
                                v-for="account in accounts"
                                :key="account.id"
                                :label="account.label"
                                :value="account.id"
                                :disabled="form.to_bank_account_id === account.id"
                            >
                            </el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item prop="to_bank_account_id">
                        <template slot="label">
                            <span class="bold">
                                {{ $l10n.__('Vers le compte') }}
                            </span>
                        </template>
                        <el-select
                            v-model="form.to_bank_account_id"
                            class="wp-10"
                            :placeholder="
                                $t.__('bankreconciliation.TransferTransactionForm.select_account')
                            "
                        >
                            <el-option
                                v-for="account in accounts"
                                :key="account.id"
                                :label="account.label"
                                :value="account.id"
                                :disabled="form.from_bank_account_id === account.id"
                            >
                            </el-option>
                        </el-select>
                    </el-form-item>
                    <div class="flex-row ai-center">
                        <span class="bold block">
                            {{ $l10n.__('en comprenant les transactions du compte entre le') }}
                        </span>
                        <el-form-item class="mb-0 pl-2" prop="daterange">
                            <el-date-picker
                                v-model="daterange"
                                mode="edit"
                                unlinkPanels
                                align="right"
                                type="daterange"
                                :defaultValue="setDefaultDate"
                                valueFormat="yyyy-MM-dd HH:mm:ss"
                                :format="datePickerFormat"
                                :rangeSeparator="$t.__('common.labels.to')"
                                :pickerOptions="pickerOptions"
                                @change="handleDateChange"
                            >
                            </el-date-picker>
                        </el-form-item>
                    </div>
                    <el-form-item class="mt-4">
                        <el-checkbox v-model="form.remove_current_transaction" :value="true">{{
                            $l10n.__("Écraser les transactions sur le compte d'arrivée")
                        }}</el-checkbox>
                    </el-form-item>

                    <div class="flex-row flex-right mt-4">
                        <CompassButton
                            :label="$t.__('common.actions.cancel')"
                            type="secondary"
                            @click="close"
                        />
                        <CompassButton
                            :label="$t.__('common.actions.transfer')"
                            type="primary"
                            class="ml-3"
                            @click="submit()"
                        />
                    </div>
                </el-form>
            </div>
        </template>
    </CompassDialog>
</template>

<script>
import SidePanel from 'vcomponents/utilities/SidePanel.vue';
import BankReconciliationImport from 'api/bankreconciliationImport';
import { createNamespacedHelpers } from 'vuex';

const { mapGetters, mapState } = createNamespacedHelpers('corp');

export default {
    components: {
        SidePanel,
    },
    data() {
        return {
            showModal: true,
            daterange: '',
            loading: false,
            setDefaultDate: '',
            form: {
                from_bank_account_id: '',
                to_bank_account_id: '',
                remove_current_transaction: false,
                first_transaction_date: null,
                last_transaction_date: null,
                daterange: '',
            },
            rules: {
                from_bank_account_id: [
                    { required: true, message: this.$l10n.__('Veuillez sélectionner un compte') },
                ],
                to_bank_account_id: [
                    { required: true, message: this.$l10n.__('Veuillez sélectionner un compte') },
                ],
                daterange: [
                    { required: true, message: this.$l10n.__('Veuillez sélectionner une période') },
                ],
            },
            accounts: [],
            baseOptions: {
                disabledDate(time) {
                    return time.getTime() > Date.now();
                },
            },
        };
    },
    computed: {
        pickerOptions() {
            return {
                ...this.baseOptions,
                disabledDate: (time) => {
                    return time.getTime() > Date.now();
                },
            };
        },
        ...mapGetters(['datePickerFormat']),
    },
    watch: {
        daterange(newval) {
            if (newval != null) {
                this.form.daterange = newval;
                this.form.first_transaction_date = newval[0];
                this.form.last_transaction_date = newval[1];
            } else {
                this.form.daterange = '';
                this.form.first_transaction_date = '';
                this.form.last_transaction_date = '';
            }
        },
    },
    created() {
        this.getBankAccounts();
        const currentDate = Date.now();
        this.setDefaultDate = currentDate.setMonth(currentDate.getMonth() - 1);
    },
    methods: {
        close() {
            this.$emit('close');
        },
        submit() {
            this.$refs.form.validate((valid) => {
                if (!valid) return false;

                BankReconciliationImport.transferTransaction(this.form)
                    .then((response) => {
                        this.close();
                    })
                    .catch((errors) => {
                        Object.values(errors).forEach((error) => Push.error(error, false));
                    });
            });
        },
        handleDateChange() {
            this.$forceUpdate();
        },
        async getBankAccounts() {
            this.loading = true;
            await BankReconciliationImport.getBankAccounts()
                .then((response) => {
                    this.accounts = response;
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    Push.error(error, false);
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.transfer-transaction-form {
    .dialog {
        &__default {
            padding-bottom: 24px;
        }
    }
}

// DatePicker
.el-date-editor.el-date-editor--daterange,
.el-date-editor.el-range-editor {
    width: 260px;
}
</style>
